import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Modal } from 'antd';
import {
    ArrowLeftIcon,
    CaretRightIcon,
    HouseIcon,
    ListIcon,
    PlusIcon,
    SquaresFourIcon,
    UploadSimpleIcon,
} from '@phosphor-icons/react';
import { toast } from 'react-toastify';
import SidebarLayout from 'components/Layout/Layout';
import {
    createFolder,
    deleteDocument,
    deleteFolder,
    getDownloadUrl,
    getLibraryContents,
    getThumbnailUrl,
    moveDocument,
    moveFolder,
    renameDocument,
    updateFolder,
    updateThumbnail,
} from 'services/library';
import type { Document, Folder, LibraryContent } from 'types/library';
import FolderCard from './components/FolderCard';
import FileCard from './components/FileCard';
import FolderModal from './components/FolderModal';
import MoveModal from './components/MoveModal';
import RenameModal from './components/RenameModal';
import ThumbnailModal from './components/ThumbnailModal';
import UploadModal from './components/UploadModal';
import './library.css';

type ViewMode = 'grid' | 'list';

interface BreadcrumbItem {
    id: number | null;
    name: string;
}

const LibraryPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const folderParam = searchParams.get('folder');
    const currentFolderId = folderParam ? Number(folderParam) : null;

    const [content, setContent] = useState<LibraryContent>({ folders: [], documents: [], current_parent_id: null, current_tab: '' });
    const STORAGE_KEY = 'admin-lib-breadcrumb';
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) return JSON.parse(saved) as BreadcrumbItem[];
        } catch {}
        return [{ id: null, name: 'Thư viện' }];
    });
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState<ViewMode>(() => (localStorage.getItem('admin-lib-view') as ViewMode) ?? 'grid');

    const [folderModal, setFolderModal] = useState<{ open: boolean; mode: 'create' | 'edit'; folder?: Folder }>({ open: false, mode: 'create' });
    const [uploadOpen, setUploadOpen] = useState(false);
    const [moveTarget, setMoveTarget] = useState<{ type: 'folder' | 'doc'; item: Folder | Document } | null>(null);
    const [renameTarget, setRenameTarget] = useState<Document | null>(null);
    const [thumbnailTarget, setThumbnailTarget] = useState<Document | null>(null);

    const isHandbookFolder = content.current_tab === 'handbook';

    const loadContent = useCallback(async (parentId: number | null) => {
        setLoading(true);
        try {
            const data = await getLibraryContents(parentId);
            setContent(data);
        } catch {
            toast.error('Không thể tải nội dung thư viện');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadContent(currentFolderId);
    }, [currentFolderId, loadContent]);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(breadcrumb));
    }, [breadcrumb]);

    useEffect(() => {
        return () => { localStorage.removeItem(STORAGE_KEY); };
    }, []);

    const navigateInto = (folder: Folder) => {
        setBreadcrumb(prev => [...prev, { id: folder.id, name: folder.name }]);
        setSearchParams({ folder: String(folder.id) });
    };

    const navigateTo = (index: number) => {
        const item = breadcrumb[index];
        setBreadcrumb(prev => prev.slice(0, index + 1));
        item.id == null ? setSearchParams({}) : setSearchParams({ folder: String(item.id) });
    };

    const setView = (mode: ViewMode) => {
        setViewMode(mode);
        localStorage.setItem('admin-lib-view', mode);
    };

    // Handlers
    const handleCreateFolder = async (name: string, color: string) => {
        try {
            const folder = await createFolder(name, color, currentFolderId);
            setContent(prev => ({ ...prev, folders: [...prev.folders, folder] }));
            setFolderModal({ open: false, mode: 'create' });
            toast.success('Tạo thư mục thành công');
        } catch {
            toast.error('Tạo thư mục thất bại');
            throw new Error();
        }
    };

    const handleUpdateFolder = async (name: string, color: string) => {
        if (!folderModal.folder) return;
        try {
            const updated = await updateFolder(folderModal.folder.id, name, color);
            setContent(prev => ({ ...prev, folders: prev.folders.map(f => f.id === updated.id ? updated : f) }));
            setFolderModal({ open: false, mode: 'edit' });
            toast.success('Cập nhật thành công');
        } catch {
            toast.error('Cập nhật thất bại');
            throw new Error();
        }
    };

    const handleDeleteFolder = (folder: Folder) => {
        Modal.confirm({
            title: 'Xóa thư mục',
            content: `Bạn có chắc muốn xóa thư mục "${folder.name}"?`,
            okText: 'Xóa', okButtonProps: { danger: true }, cancelText: 'Hủy',
            onOk: async () => {
                await deleteFolder(folder.id);
                setContent(prev => ({ ...prev, folders: prev.folders.filter(f => f.id !== folder.id) }));
                toast.success('Đã xóa thư mục');
            },
        });
    };

    const handleDeleteDocument = (doc: Document) => {
        Modal.confirm({
            title: 'Xóa tài liệu',
            content: `Bạn có chắc muốn xóa "${doc.name}"?`,
            okText: 'Xóa', okButtonProps: { danger: true }, cancelText: 'Hủy',
            onOk: async () => {
                await deleteDocument(doc.id);
                setContent(prev => ({ ...prev, documents: prev.documents.filter(d => d.id !== doc.id) }));
                toast.success('Đã xóa tài liệu');
            },
        });
    };

    const handleDownload = async (doc: Document) => {
        try {
            const url = await getDownloadUrl(doc.id);
            window.open(url, '_blank');
        } catch {
            toast.error('Không thể tải file');
        }
    };

    const handleRenameDocument = async (newName: string) => {
        if (!renameTarget) return;
        try {
            const updated = await renameDocument(renameTarget.id, newName, renameTarget.folder_id);
            setContent(prev => ({ ...prev, documents: prev.documents.map(d => d.id === updated.id ? updated : d) }));
            toast.success('Đổi tên thành công');
        } catch {
            toast.error('Đổi tên thất bại');
            throw new Error();
        }
    };

    const handleChangeThumbnail = async (file: File) => {
        if (!thumbnailTarget) return;
        try {
            const updated = await updateThumbnail(thumbnailTarget.id, file);
            setContent(prev => ({ ...prev, documents: prev.documents.map(d => d.id === updated.id ? updated : d) }));
            toast.success('Cập nhật thumbnail thành công');
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Cập nhật thumbnail thất bại');
            throw new Error();
        }
    };

    const handleMove = async (targetFolderId: number | null) => {
        if (!moveTarget) return;
        try {
            if (moveTarget.type === 'doc') {
                await moveDocument((moveTarget.item as Document).id, targetFolderId);
                setContent(prev => ({ ...prev, documents: prev.documents.filter(d => d.id !== (moveTarget.item as Document).id) }));
            } else {
                await moveFolder((moveTarget.item as Folder).id, targetFolderId);
                setContent(prev => ({ ...prev, folders: prev.folders.filter(f => f.id !== (moveTarget.item as Folder).id) }));
            }
            toast.success('Di chuyển thành công');
            setMoveTarget(null);
        } catch {
            toast.error('Di chuyển thất bại');
            throw new Error();
        }
    };

    const isEmpty = !loading && !content.folders.length && !content.documents.length;

    return (
        <SidebarLayout>
            <div className="space-y-4">

                {/* Toolbar */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                    {/* Breadcrumb */}
                    <div className="flex items-center gap-1 flex-wrap">
                        {breadcrumb.map((item, index) => (
                            <React.Fragment key={index}>
                                {index > 0 && <CaretRightIcon size={13} className="text-text-muted" />}
                                <button
                                    onClick={() => navigateTo(index)}
                                    className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-sm transition hover:bg-bg-hover ${index === breadcrumb.length - 1 ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}
                                >
                                    {index === 0 && <HouseIcon size={13} />}
                                    {item.name}
                                </button>
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                        {breadcrumb.length > 1 && (
                            <button onClick={() => navigateTo(breadcrumb.length - 2)}
                                className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-hover transition">
                                <ArrowLeftIcon size={14} /> Quay lại
                            </button>
                        )}
                        <div className="flex rounded-md border border-border overflow-hidden">
                            <button onClick={() => setView('grid')}
                                className={`flex items-center px-2.5 py-1.5 transition ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-text-secondary hover:bg-bg-hover'}`}>
                                <SquaresFourIcon size={16} />
                            </button>
                            <button onClick={() => setView('list')}
                                className={`flex items-center px-2.5 py-1.5 transition ${viewMode === 'list' ? 'bg-primary text-white' : 'text-text-secondary hover:bg-bg-hover'}`}>
                                <ListIcon size={16} />
                            </button>
                        </div>
                        {!isHandbookFolder && (
                            <button onClick={() => setFolderModal({ open: true, mode: 'create' })}
                                className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-hover transition">
                                <PlusIcon size={14} /> Thư mục
                            </button>
                        )}
                        <button onClick={() => setUploadOpen(true)}
                            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover transition">
                            <UploadSimpleIcon size={14} />
                            {isHandbookFolder ? 'Upload Handbook' : 'Tải lên'}
                        </button>
                    </div>
                </div>


                {/* Content */}
                {loading ? (
                    <div className="flex items-center justify-center py-20 text-sm text-text-muted">Đang tải...</div>
                ) : isEmpty ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-20 text-text-muted">
                        <svg width="48" height="48" viewBox="0 0 122 90" fill="none" opacity="0.2">
                            <path d="M120.085 14.1861V83.5406C120.085 87.1075 117.184 90 113.607 90H8.55106C4.97387 90 2.073 87.1075 2.073 83.5406V6.45939C2.073 2.89252 4.97387 0 8.55106 0H31.6246C33.6237 0 35.5114 0.921109 36.7384 2.49462L40.8196 7.72672H113.607C117.184 7.72672 120.085 10.6192 120.085 14.1861Z" fill="currentColor" />
                        </svg>
                        <p className="text-sm">Chưa có tài liệu nào</p>
                        <button onClick={() => setUploadOpen(true)}
                            className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover transition">
                            <UploadSimpleIcon size={14} /> Tải lên ngay
                        </button>
                    </div>
                ) : viewMode === 'grid' ? (
                    <div className="space-y-6">
                        {content.folders.length > 0 && (
                            <div>
                                <p className="lib-section-label">Thư mục</p>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
                                    {content.folders.map(folder => (
                                        <FolderCard key={folder.id} folder={folder} variant="grid"
                                            onClick={() => navigateInto(folder)}
                                            onEdit={() => setFolderModal({ open: true, mode: 'edit', folder })}
                                            onDelete={() => handleDeleteFolder(folder)}
                                            onMove={() => setMoveTarget({ type: 'folder', item: folder })}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        {content.documents.length > 0 && (
                            <div>
                                <p className="lib-section-label">Tệp</p>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                                    {content.documents.map(doc => (
                                        <FileCard key={doc.id} doc={doc} variant="grid"
                                            onDownload={() => handleDownload(doc)}
                                            onDelete={() => handleDeleteDocument(doc)}
                                            onMove={() => setMoveTarget({ type: 'doc', item: doc })}
                                            onRename={() => setRenameTarget(doc)}
                                            onChangeThumbnail={() => setThumbnailTarget(doc)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-6">
                        {content.folders.length > 0 && (
                            <div>
                                <p className="lib-section-label">Thư mục</p>
                                <div className="lib-list-header">
                                    <span /><span>Tên</span><span>Kích thước</span><span>Ngày tạo</span><span />
                                </div>
                                {content.folders.map(folder => (
                                    <FolderCard key={folder.id} folder={folder} variant="list"
                                        onClick={() => navigateInto(folder)}
                                        onEdit={() => setFolderModal({ open: true, mode: 'edit', folder })}
                                        onDelete={() => handleDeleteFolder(folder)}
                                        onMove={() => setMoveTarget({ type: 'folder', item: folder })}
                                    />
                                ))}
                            </div>
                        )}
                        {content.documents.length > 0 && (
                            <div>
                                <p className="lib-section-label">Tệp</p>
                                <div className="lib-list-header">
                                    <span /><span>Tên</span><span>Kích thước</span><span>Ngày tạo</span><span />
                                </div>
                                {content.documents.map(doc => (
                                    <FileCard key={doc.id} doc={doc} variant="list"
                                        onDownload={() => handleDownload(doc)}
                                        onDelete={() => handleDeleteDocument(doc)}
                                        onMove={() => setMoveTarget({ type: 'doc', item: doc })}
                                        onRename={() => setRenameTarget(doc)}
                                        onChangeThumbnail={() => setThumbnailTarget(doc)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <FolderModal
                open={folderModal.open}
                title={folderModal.mode === 'create' ? 'Tạo thư mục' : 'Chỉnh sửa thư mục'}
                initialName={folderModal.folder?.name}
                initialColor={folderModal.folder?.color}
                onConfirm={folderModal.mode === 'create' ? handleCreateFolder : handleUpdateFolder}
                onClose={() => setFolderModal(prev => ({ ...prev, open: false }))}
            />

            <UploadModal
                open={uploadOpen}
                folderId={currentFolderId}
                onClose={() => setUploadOpen(false)}
                onSuccess={doc => setContent(prev => ({ ...prev, documents: [...prev.documents, doc] }))}
            />

            <MoveModal
                open={moveTarget !== null}
                itemName={moveTarget?.item.name ?? ''}
                currentFolderId={currentFolderId}
                onConfirm={handleMove}
                onClose={() => setMoveTarget(null)}
            />

            <RenameModal
                open={renameTarget !== null}
                currentName={renameTarget?.name ?? ''}
                onConfirm={handleRenameDocument}
                onClose={() => setRenameTarget(null)}
            />

            <ThumbnailModal
                open={thumbnailTarget !== null}
                currentThumbnailUrl={thumbnailTarget?.thumbnail_path ? getThumbnailUrl(thumbnailTarget.id) : null}
                onConfirm={handleChangeThumbnail}
                onClose={() => setThumbnailTarget(null)}
            />
        </SidebarLayout>
    );
};

export default LibraryPage;
